import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import apiService from '@/services/api';
import sitesService from '@/services/sites';
import { useAuth } from './AuthContext';

interface Tenant {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
}

interface Site {
  id: number;
  name: string;
  code: string;
  site_type: string;
  address: string;
  is_active: boolean;
  tenant: {
    id: number;
    name: string;
    code: string;
  };
}

interface TenantContextType {
  activeTenant: Tenant | null;
  activeSite: Site | null;
  availableSites: Site[];
  switchSite: (siteCode: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [activeTenant, setActiveTenant] = useState<Tenant | null>(null);
  const [activeSite, setActiveSite] = useState<Site | null>(null);
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadUserSites = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Utiliser le service sites pour récupérer les sites accessibles
      const sites = await sitesService.getMySites();
      
      // Filtrer les sites valides (avec tenant)
      const validSites = sites.filter(site => 
        site && 
        site.id && 
        site.tenant && 
        site.tenant.id && 
        site.code && 
        site.name
      );
      
      setAvailableSites(validSites);
      
      // Définir le site actif (premier par défaut)
      if (validSites.length > 0 && !activeSite) {
        const defaultSite = validSites.find(s => s.is_default) || validSites[0];
        
        if (defaultSite) {
          setActiveSite(defaultSite);
          setActiveTenant({
            ...defaultSite.tenant,
            is_active: true // Valeur par défaut
          });
          
          // Définir les headers pour les prochaines requêtes
          apiService.setTenantHeaders(defaultSite.tenant.id, defaultSite.id);
          
          // Sauvegarder dans localStorage
          localStorage.setItem('activeSiteCode', defaultSite.code);
          localStorage.setItem('activeSiteInfo', JSON.stringify(defaultSite));
          
        }
      } else if (validSites.length === 0) {
        console.warn('Aucun site valide trouvé');
        setError('Aucun site accessible trouvé');
      }
    } catch (err) {
      setError('Erreur lors du chargement des sites');
      console.error('Error loading user sites:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const switchSite = async (siteCode: string) => {
    try {
      setError(null);
      const site = availableSites.find(s => s.code === siteCode);
      if (!site) {
        throw new Error('Site non trouvé');
      }
      
      // Vérifier que le site et le tenant sont valides
      if (site && site.tenant && site.tenant.id) {
        setActiveSite(site);
        setActiveTenant({
          ...site.tenant,
          is_active: true // Valeur par défaut
        });
        
        // Mettre à jour les headers pour les prochaines requêtes
        apiService.setTenantHeaders(site.tenant.id, site.id);
        
        // Sauvegarder dans localStorage
        localStorage.setItem('activeSiteCode', siteCode);
        localStorage.setItem('activeSiteInfo', JSON.stringify(site));
      } else {
        throw new Error('Site ou tenant invalide');
      }
      
    } catch (err) {
      setError('Erreur lors du changement de site');
      console.error('Error switching site:', err);
    }
  };

  useEffect(() => {
    // Ne charger les sites que si l'utilisateur est authentifié
    if (isAuthenticated && !authLoading) {
      loadUserSites();
    } else if (!authLoading) {
      // Si non authentifié, nettoyer les données et arrêter le loading
      setAvailableSites([]);
      setActiveSite(null);
      setActiveTenant(null);
      setError(null);
      setIsLoading(false);
      
      // Nettoyer les headers et localStorage
      apiService.clearTenantHeaders();
      localStorage.removeItem('activeSiteCode');
      localStorage.removeItem('activeSiteInfo');
    }
  }, [isAuthenticated, authLoading]);

  // Effet séparé pour restaurer le site actif quand availableSites change
  useEffect(() => {
    if (availableSites.length > 0) {
      // Restaurer le site actif depuis localStorage
      const savedSiteCode = localStorage.getItem('activeSiteCode');
      if (savedSiteCode) {
        const savedSite = availableSites.find(s => s.code === savedSiteCode);
        if (savedSite) {
          setActiveSite(savedSite);
          setActiveTenant({
            ...savedSite.tenant,
            is_active: true // Valeur par défaut
          });
          apiService.setTenantHeaders(savedSite.tenant.id, savedSite.id);
          localStorage.setItem('activeSiteInfo', JSON.stringify(savedSite));
        }
      }
    }
  }, [availableSites]);


  return (
    <TenantContext.Provider
      value={{
        activeTenant,
        activeSite,
        availableSites,
        switchSite,
        isLoading,
        error,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

